import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Element } from "react-scroll";
import emailjs from "@emailjs/browser";
import { validateEmail } from "../utils/ValidateUtils";
import Toast from "../Components/Toast";
import landingWebm from "../img/product/landing.webm";
import healthHubLogo from "../img/partners/health_hub_tirol.svg";
import startupTirolLogo from "../img/partners/logo-start-up-tirol-web.png";
import b2GreenHubLogo from "../img/partners/b2-green-hub.svg";
import standortTirolLogo from "../img/partners/tirol_cluster.svg";
import AWSLogo from "../img/partners/AWS_logo.svg";
import InncubatorLogo from "../img/partners/inncubator_logo.png";
import "../styles/landing.scss";
import { getCanonicalUrl } from "../utils/seoConfig";

function Home() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openFaqIndex, setOpenFaqIndex] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [company, setCompany] = useState("");
  const [message, setMessage] = useState("");
  const [gdprConsent, setGdprConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState({
    show: false,
    message: "",
    type: "success",
  });

  const expertTypes = useMemo(
    () => [
      "Manufacturing Engineers",
      "Quality Experts",
      "Process Engineers",
      "Operations Experts",
      "Energy Analysts",
      "Utilities Engineers",
      "Power Systems Engineers",
      "Grid Engineers",
      "Renewable Energy Engineers",
      "Financial Analysts",
      "Trading Analysts",
      "Risk Analysts",
      "Sales Analysts",
      "Marketing Analysts",
      "E-commerce Experts",
      "Revenue Analysts",
      "Customer Analysts",
      "Industrial Engineers",
      "Agriculture Experts",
      "Environmental Monitoring Experts",
      "Greenhouse Management Experts",
      "Logistics Experts",
      "Supply Chain Experts",
      "IT Infrastructure Engineers",
      "IoT Engineers",
      "Network Operations Engineers",
      "Systems Monitoring Experts",
      "Weather Analysts",
      "Air Quality Analysts",
      "Meteorology Analysts",
    ],
    []
  );

  const [currentExpertIndex, setCurrentExpertIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [charIndex, setCharIndex] = useState(0);

  React.useEffect(() => {
    emailjs.init(process.env.REACT_APP_EMAIL_JS_PUBLIC_KEY);
  }, []);

  React.useEffect(() => {
    const currentText = expertTypes[currentExpertIndex];
    const typingSpeed = isDeleting ? 50 : 100; // Brže briše, sporije kuca
    const pauseAfterTyping = 2000; // Pauza nakon što se ispiše cela reč

    let timeout;

    if (isDeleting) {
      // Backspace efekat - briše karakter po karakter
      if (charIndex > 0) {
        timeout = setTimeout(() => {
          setDisplayedText(currentText.substring(0, charIndex - 1));
          setCharIndex(charIndex - 1);
        }, typingSpeed);
      } else {
        // Kada se sve obriše, pređi na sledeću reč
        setIsDeleting(false);
        setCurrentExpertIndex(
          (prevIndex) => (prevIndex + 1) % expertTypes.length
        );
      }
    } else {
      // Typewriter efekat - ispisuje karakter po karakter
      if (charIndex < currentText.length) {
        timeout = setTimeout(() => {
          setDisplayedText(currentText.substring(0, charIndex + 1));
          setCharIndex(charIndex + 1);
        }, typingSpeed);
      } else {
        // Kada se cela reč ispiše, sačekaj pa počni da brišeš
        timeout = setTimeout(() => {
          setIsDeleting(true);
        }, pauseAfterTyping);
      }
    }

    return () => {
      if (timeout) clearTimeout(timeout);
    };
  }, [charIndex, isDeleting, currentExpertIndex, expertTypes]);

  // Resetuj kada se promeni index
  React.useEffect(() => {
    setDisplayedText("");
    setCharIndex(0);
    setIsDeleting(false);
  }, [currentExpertIndex]);

  const handleSend = async (e) => {
    if (!validateEmail(email)) {
      setToast({
        show: true,
        message: t("InvalidEmail"),
        type: "error",
      });
      return;
    }

    e.preventDefault();
    setIsLoading(true);

    const serviceId = process.env.REACT_APP_EMAIL_JS_SERVICE_ID;
    const templateId = process.env.REACT_APP_EMAIL_JS_TEMPLATE_ID;

    try {
      await emailjs.send(serviceId, templateId, {
        name: fullName,
        email: email,
        phone: phone,
        company: company,
        message: message,
      });
      setToast({
        show: true,
        message: t("EmailSuccessfullySent"),
        type: "success",
      });
    } catch (error) {
      console.log(error);
      setToast({
        show: true,
        message: t("EmailError"),
        type: "error",
      });
    } finally {
      setIsLoading(false);
      setFullName("");
      setEmail("");
      setPhone("");
      setCompany("");
      setMessage("");
      setGdprConsent(false);
    }
  };

  const handleCloseToast = () => {
    setToast({ ...toast, show: false });
  };

  const faqs = [
    {
      question: t("HomeFaq1Question"),
      answer: t("HomeFaq1Answer"),
    },
    {
      question: t("HomeFaq2Question"),
      answer: t("HomeFaq2Answer"),
    },
    {
      question: t("HomeFaq3Question"),
      answer: t("HomeFaq3Answer"),
    },
    {
      question: t("HomeFaq4Question"),
      answer: t("HomeFaq4Answer"),
    },
    {
      question: t("HomeFaq5Question"),
      answer: t("HomeFaq5Answer"),
    },
  ];

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  const partners = [
    {
      id: 2,
      name: "Health Hub Tirol",
      logoUrl: healthHubLogo,
      link: "https://www.healthhub.tirol/",
    },
    {
      id: 3,
      name: "Startup Tirol",
      logoUrl: startupTirolLogo,
      link: "https://www.startup.tirol/",
    },
    {
      id: 6,
      name: "AWS",
      logoUrl: AWSLogo,
      link: "https://aws.amazon.com/",
      className: "height-150 mt-125",
    },
    {
      id: 7,
      name: "INNCUBATOR",
      logoUrl: InncubatorLogo,
      link: "https://inncubator.at/",
      className: "height-100 mt-150",
    },
    {
      id: 5,
      name: "Standort Tirol",
      logoUrl: standortTirolLogo,
      link: "https://www.standort-tirol.at/",
      className: "height-150 mt-125",
    },
    {
      id: 4,
      name: "B2 Green Hub",
      logoUrl: b2GreenHubLogo,
      link: "https://b2greenhub.eu/",
    },
  ];

  return (
    <>
      <Helmet>
        <title>{t("HomePageTitle")}</title>
        <meta name="description" content={t("HomePageDescription")} />
        <meta name="keywords" content={t("HomePageKeywords")} />
        <link rel="canonical" href={getCanonicalUrl("/")} />
      </Helmet>
      <div className="App">
        <section className="section hero-section">
          <div className="hero-content">
            <h1 className="hero-title">{t("HeroTitle")}</h1>
            <p className="hero-subtitle">
              {t("HeroSubtitle")}
              <br />
              {t("MadeFor")}{" "}
              <span className="rotating-expert">
                {displayedText}
                <span className="cursor">|</span>
              </span>
            </p>
            <div className="hero-cta-buttons">
              <button
                className="primary-button"
                onClick={() => {
                  const productsSection =
                    document.querySelector(".products-showcase");
                  if (productsSection) {
                    productsSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                {t("ExploreProducts")}
              </button>
              <button
                className="secondary-button"
                onClick={() => {
                  const contactSection = document.getElementById("contact");
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: "smooth" });
                  }
                }}
              >
                {t("Contact")} {t("Us")}
              </button>
            </div>
            <div className="hero-video">
              <video
                autoPlay
                loop
                muted
                playsInline
                className="hero-video-element"
              >
                <source src={landingWebm} type="video/webm" />
              </video>
            </div>
          </div>
        </section>

        <section className="section partners-section">
          <h2 className="partners-title">
            {t("OurPartnersTitle")} <span>{t("PartnersSpan")}</span>
          </h2>
          <div
            className={`partners-container ${isPaused ? "paused" : ""}`}
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
          >
            <div className="partners-track">
              {[...partners, ...partners, ...partners].map((partner, index) => (
                <a
                  key={`partner-${index}`}
                  href={partner.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="partner-item"
                >
                  <img
                    src={partner.logoUrl}
                    alt={`${partner.name} logo`}
                    className={`partner-logo ${partner.className || ""}`}
                  />
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="section products-showcase">
          <div className="products-showcase-wrapper">
            <h2 className="products-showcase-title">
              {t("ExploreOurSolutions")}
            </h2>
            <div className="products-showcase-grid">
              <div
                className="product-panel"
                onClick={() => navigate("/products/labeling")}
              >
                <div className="product-panel-content">
                  <div className="product-panel-header">
                    <div className="product-panel-badge">
                      <span className="badge-line"></span>
                      <span className="badge-text">{t("B2CAndB2B")}</span>
                    </div>
                    <h3 className="product-panel-title">
                      {t("TimeSeriesLabeling")}
                    </h3>
                    <p className="product-panel-description">
                      {t("TimeSeriesLabelingDescription")}
                    </p>
                  </div>
                  <div className="product-panel-preview">
                    <div className="product-preview-item">
                      <div className="preview-content">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 15V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V15"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 10L12 15L17 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M12 15V3"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{t("CSVImportExport")}</span>
                      </div>
                    </div>
                    <div className="product-preview-item">
                      <div className="preview-content">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M11 4H4C3.46957 4 2.96086 4.21071 2.58579 4.58579C2.21071 4.96086 2 5.46957 2 6V20C2 20.5304 2.21071 21.0391 2.58579 21.4142C2.96086 21.7893 3.46957 22 4 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V13"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M18.5 2.5C18.8978 2.10217 19.4374 1.87868 20 1.87868C20.5626 1.87868 21.1022 2.10217 21.5 2.5C21.8978 2.89782 22.1213 3.43739 22.1213 4C22.1213 4.56261 21.8978 5.10217 21.5 5.5L12 15L8 16L9 12L18.5 2.5Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{t("EventAnnotation")}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="product-panel-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/products/labeling");
                    }}
                  >
                    {t("ExploreProduct")} →
                  </button>
                </div>
              </div>

              <div
                className="product-panel"
                onClick={() => navigate("/products/anomaly-detection")}
              >
                <div className="product-panel-content">
                  <div className="product-panel-header">
                    <div className="product-panel-badge b2b">
                      <span className="badge-line"></span>
                      <span className="badge-text">{t("B2BOnly")}</span>
                    </div>
                    <h3 className="product-panel-title">
                      {t("AnomalyDetection")}
                    </h3>
                    <p className="product-panel-description">
                      {t("AnomalyDetectionDescription")}
                    </p>
                  </div>
                  <div className="product-panel-preview">
                    <div className="product-preview-item">
                      <div className="preview-content">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3 3V21H21"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M7 16L12 11L16 15L21 10"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{t("AutomatedAnomalyDetection")}</span>
                      </div>
                    </div>
                    <div className="product-preview-item">
                      <div className="preview-content">
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M14 2V8H20"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 13H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M16 17H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <path
                            d="M10 9H9H8"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>{t("BusinessIntelligenceReport")}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="product-panel-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/products/anomaly-detection");
                    }}
                  >
                    {t("ExploreProduct")} →
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="section faq-section">
          <div className="faq-wrapper">
            <h2 className="section-title">{t("FrequentlyAskedQuestions")}</h2>
            <p className="section-subtitle">{t("FAQSubtitle")}</p>
            <div className="faq-list">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className={`faq-item ${openFaqIndex === index ? "open" : ""}`}
                >
                  <button
                    className="faq-question"
                    onClick={() => toggleFaq(index)}
                  >
                    <span>{faq.question}</span>
                    <svg
                      className="faq-icon"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 9L12 15L18 9"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                  <div className="faq-answer">
                    <p>{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section
          className="section testimonials-section"
          aria-labelledby="testimonials-heading"
        >
          <div className="testimonials-inner">
            <p className="testimonials-overline">{t("TestimonialsOverline")}</p>
            <h2 id="testimonials-heading" className="section-title">
              {t("TestimonialsTitle")}
            </h2>
            <p className="section-subtitle">{t("TestimonialsSubtitle")}</p>
            <div className="testimonials-divider" aria-hidden />
            <div className="testimonials-card">
              <blockquote className="testimonials-quote">
                <p>{t("HomeTestimonial1Quote")}</p>
              </blockquote>
              <div className="testimonials-apost" aria-hidden>
                <svg
                  className="testimonials-apost-icon"
                  width="56"
                  height="49"
                  viewBox="0 0 56 49"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.6308 49C8.04103 49 5.24103 47.7093 3.23077 45.1279C1.07692 42.5466 0 39.033 0 34.5873C0 27.9905 1.4359 21.9673 4.30769 16.5177C7.32308 11.0681 12.1333 5.97711 18.7385 1.24459C20.4615 -0.0460955 21.8256 -0.332915 22.8308 0.384132C23.4051 0.814364 23.6923 1.45971 23.6923 2.32016C23.6923 3.03721 23.1897 3.75426 22.1846 4.47131C17.3026 8.63019 14.1436 11.8569 12.7077 14.1515C11.4154 16.3026 10.7692 18.5972 10.7692 21.0351C10.7692 23.6165 11.3436 25.6242 12.4923 27.0583C13.641 28.349 14.9333 29.4963 16.3692 30.5002C17.8051 31.3606 19.0974 32.4362 20.2462 33.7269C21.3949 35.0176 21.9692 36.9536 21.9692 39.535C21.9692 42.1163 21.0359 44.3392 19.1692 46.2035C17.4462 48.0678 14.9333 49 11.6308 49ZM43.9385 49C40.3487 49 37.4769 47.7093 35.3231 45.1279C33.3128 42.5466 32.3077 39.033 32.3077 34.5873C32.3077 27.9905 33.7436 21.9673 36.6154 16.5177C39.6308 11.0681 44.441 5.97711 51.0462 1.24459C52.7692 -0.0460955 54.1333 -0.332915 55.1385 0.384132C55.7128 0.814364 56 1.45971 56 2.32016C56 3.03721 55.4974 3.75426 54.4923 4.47131C49.6103 8.63019 46.4513 11.8569 45.0154 14.1515C43.7231 16.3026 43.0769 18.5972 43.0769 21.0351C43.0769 23.6165 43.6513 25.6242 44.8 27.0583C45.9487 28.349 47.241 29.4963 48.6769 30.5002C50.1128 31.3606 51.4051 32.4362 52.5539 33.7269C53.7026 35.0176 54.2769 36.9536 54.2769 39.535C54.2769 42.1163 53.3436 44.3392 51.4769 46.2035C49.7538 48.0678 47.241 49 43.9385 49Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
            </div>
          </div>
        </section>

        <Element name="contact" id="contact" className="section contact">
          {toast.show && (
            <div className="toast-container">
              <Toast
                message={toast.message}
                type={toast.type}
                onClose={handleCloseToast}
              />
            </div>
          )}
          <div className="contact-wrapper">
            <div className="contact-container">
              <div className="contact-info">
                <div className="info-card">
                  <h2 className="section-title">
                    {t("Contact")} <span>{t("Us")}</span>
                  </h2>
                  <p className="info-text">{t("ReadyToTransform")}</p>
                  <div className="contact-details">
                    <div className="detail-item">
                      <svg className="icon" viewBox="0 0 24 24">
                        <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
                      </svg>
                      <span>{t("Location")}</span>
                    </div>
                    <div className="detail-item">
                      <svg className="icon" viewBox="0 0 24 24">
                        <path d="M20,8L12,13L4,8V6L12,11L20,6M20,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V6C22,4.89 21.1,4 20,4Z" />
                      </svg>
                      <a href="mailto:info@predsense.ai">info@predsense.ai</a>
                    </div>
                    <div className="detail-item">
                      <svg className="icon linkedin-icon" viewBox="0 0 24 24">
                        <path d="M19,3A2,2 0 0,1 21,5V19A2,2 0 0,1 19,21H5A2,2 0 0,1 3,19V5A2,2 0 0,1 5,3H19M18.5,18.5V13.2A3.26,3.26 0 0,0 15.24,9.94C14.39,9.94 13.4,10.46 12.92,11.24V10.13H10.13V18.5H12.92V13.57C12.92,12.8 13.54,12.17 14.31,12.17A1.4,1.4 0 0,1 15.71,13.57V18.5H18.5M6.88,8.56A1.68,1.68 0 0,0 8.56,6.88C8.56,5.95 7.81,5.19 6.88,5.19A1.69,1.69 0 0,0 5.19,6.88C5.19,7.81 5.95,8.56 6.88,8.56M8.27,18.5V10.13H5.5V18.5H8.27Z" />
                      </svg>
                      <a
                        href="https://www.linkedin.com/company/predsense"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        LinkedIn
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="contact-form-wrapper">
                {isLoading && (
                  <div className="loader-overlay">
                    <div className="loader"></div>
                  </div>
                )}
                <form className="contact-form" onSubmit={handleSend}>
                  <div className="form-row">
                    <div className="form-group">
                      <label>{t("FullName")} *</label>
                      <input
                        required
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder={t("EnterYourFullName")}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t("CompanyName")} *</label>
                      <input
                        required
                        type="text"
                        value={company}
                        onChange={(e) => setCompany(e.target.value)}
                        placeholder={t("EnterYourCompanyName")}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>{t("Email")} *</label>
                      <input
                        required
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder={t("EnterYourEmail")}
                      />
                    </div>
                    <div className="form-group">
                      <label>{t("Phone")}</label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder={t("EnterYourPhoneNumber")}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>{t("Message")} *</label>
                    <div className="textarea-container">
                      <textarea
                        required
                        rows="4"
                        maxLength={1000}
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder={t("TellUsAboutYourNeeds")}
                      />
                      <div className="char-counter">
                        {message.length} / 1000
                      </div>
                    </div>
                  </div>

                  <div className="form-group checkbox-inline">
                    <input
                      type="checkbox"
                      id="gdpr-consent"
                      checked={gdprConsent}
                      onChange={(e) => setGdprConsent(e.target.checked)}
                      required
                    />
                    <label htmlFor="gdpr-consent">
                      {t("IAgreeToPredsense")}{" "}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("PrivacyAndTerms")}
                      </a>
                    </label>
                  </div>

                  <button type="submit" className="submit-button">
                    {t("SendMessage")}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </Element>
      </div>
    </>
  );
}

export default Home;
